<template>
  <div
    class="relative h-screen z-30"
    :class="{
      'w-[82px] p-2 pr-0': !isExpanded,
      'w-[296px]': isExpanded,
    }"
    data-sentry-unmask
  >
    <div
      class="flex flex-col h-full px-4 divide-y divide-gray-200 bg-white border-gray-200"
      :class="{
        'border rounded-xl shadow-xs': !isExpanded,
        'border-r': isExpanded,
      }"
    >
      <div
        class="flex flex-col"
        :class="{
          'items-center py-4 gap-2': !isExpanded,
          'py-6 gap-4': isExpanded,
        }"
      >
        <div class="flex flex-wrap gap-3 items-center justify-between px-1">
          <sion-logo
            :to="logoRoute"
            :compact="!isExpanded"
            :variant="brand"
          />

          <div :class="['flex', { 'gap-2': !isLocked, 'flex-col-reverse': !isExpanded }]">
            <app-tooltip
              heading="Search"
              :placement="!isExpanded ? 'right' : 'bottom'"
            >
              <router-link
                :to="{ name: 'search', query: { ...route.query } }"
                class="btn btn-square btn-secondary size-8"
              >
                <app-icon
                  name="search"
                  class="text-gray-400"
                />
              </router-link>
            </app-tooltip>
            <app-tooltip
              heading="Toggle sidebar"
              :placement="!isExpanded ? 'right' : 'bottom'"
            >
              <button
                v-if="!isLocked"
                class="btn btn-square btn-secondary size-8"
                @click="toggleSidebar"
              >
                <app-icon
                  name="layout-left"
                  class="text-gray-400"
                />
              </button>
            </app-tooltip>
          </div>
        </div>
      </div>

      <div
        class="flex flex-col justify-between grow"
        :class="{ 'py-6': isExpanded, 'py-4': !isExpanded }"
      >
        <list-group :label="isExpanded ? 'General' : undefined">
          <template
            v-for="page in topPages[brand]"
            :key="page.name"
          >
            <sidebar-list-item
              v-if="page.isVisible"
              :label="page.name"
              :compact="!isExpanded"
              :to="page.path"
              :on-navigate="getResetAction(page)"
            >
              <template #prefix>
                <app-icon
                  :name="page.icon"
                  class="text-gray-500"
                />
              </template>
            </sidebar-list-item>
          </template>
        </list-group>

        <div
          class="flex flex-col items-center gap-4"
          :class="{ 'items-start': isExpanded }"
        >
          <list-group>
            <template
              v-for="page in bottomPages[brand]"
              :key="page.name"
            >
              <sidebar-list-item
                v-if="page.isVisible"
                :label="page.name"
                :compact="!isExpanded"
                :to="page.path"
                :on-navigate="getResetAction(page)"
              >
                <template #prefix>
                  <app-icon
                    :name="page.icon"
                    class="text-gray-500"
                  />
                </template>
              </sidebar-list-item>
            </template>
          </list-group>

          <agency-user-dropdown
            v-if="brand === SionUserTypes.AGENCY"
            :compact="!isExpanded"
            :card="isExpanded"
          />
          <supplier-user-dropdown
            v-if="brand === SionUserTypes.SUPPLIER"
            :compact="!isExpanded"
            :card="isExpanded"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useAppConfigStore } from '@/stores/appConfig.js';
import { useStatementsStore } from '@/stores/statements';
import { useTransfersStore } from '@/stores/transfers';
import { useAgenciesStore } from '@/stores/agencies';
import { SionUserTypes } from '@/constants/sion.js';

import SionLogo from '@/components/logos/SionLogo.vue';
import ListGroup from '@/components/ui/list/ListGroup.vue';
import SidebarListItem from '@/components/sidebar/SidebarListItem.vue';
import SupplierUserDropdown from '@/components/user/SupplierUserDropdown.vue';
import AgencyUserDropdown from '@/components/user/AgencyUserDropdown.vue';

defineOptions({
  name: 'AppSidebar',
});

const props = defineProps({
  brand: {
    type: String,
    validator: (val) => Object.keys(SionUserTypes).includes(val),
    default: SionUserTypes.SUPPLIER,
  },
});

const route = useRoute();
const authStore = useAuthStore();
const appConfig = useAppConfigStore();
const statementsStore = useStatementsStore();
const transfersStore = useTransfersStore();
const agenciesStore = useAgenciesStore();

const isExpanded = ref(route.meta.sidebarExpanded ?? true);
const isLocked = ref(route.meta.sidebarLocked ?? false);

const hasManySuppliers = computed(() => authStore.suppliers.length > 1);

const logoRoute = computed(() => {
  const routes = {
    [SionUserTypes.SUPPLIER]: { name: 'statements' },
    [SionUserTypes.AGENCY]: { name: 'agency-home' },
  };
  return routes[props.brand];
});

const topPages = computed(() => {
  return {
    [SionUserTypes.SUPPLIER]: [
      {
        icon: 'file-check',
        name: 'Statements',
        path: '/statements',
        isVisible: authStore.hasPermission('statement:view'),
      },
      {
        icon: 'bank-note',
        name: 'Transfers',
        path: '/transfers',
        isVisible: authStore.hasPermission('transfer:view'),
      },
      {
        icon: 'building',
        name: 'Agencies',
        path: '/agencies',
        isVisible: authStore.hasPermission('agency:view'),
      },
    ],
    [SionUserTypes.AGENCY]: [
      {
        icon: 'home-three',
        name: 'Home',
        path: '/home',
        isVisible: authStore.hasPermission('agency:view'),
      },
      {
        icon: 'bank-note',
        name: 'Transfers',
        path: '/transfers',
        isVisible: authStore.hasPermission('transfer:view'),
      },
    ],
  };
});

const bottomPages = computed(() => {
  return {
    [SionUserTypes.SUPPLIER]: [
      {
        icon: 'beaker-two',
        name: 'Simulations',
        path: '/simulations',
        isVisible: authStore.hasPermission('simulate') && !appConfig.isProduction,
      },
      {
        icon: 'settings',
        name: 'Account settings',
        path: hasManySuppliers.value ? '/suppliers' : `/suppliers/${authStore.supplierId}/details`,
        isVisible: hasManySuppliers.value
          ? authStore.hasAnyPermission(['transfer_sender:view', 'room_type:view', 'rate_code:view', 'user:list:*'])
          : authStore.hasPermission('supplier:view'),
      },
    ],
    [SionUserTypes.AGENCY]: [
      {
        icon: 'beaker-two',
        name: 'Simulations',
        path: '/agency-simulations',
        isVisible: authStore.hasPermission('simulate') && !appConfig.isProduction,
      },
      {
        icon: 'settings',
        name: 'Account settings',
        path: '/account-settings',
        isVisible: authStore.hasAnyPermission(['transfer_recipient:view']),
      },
    ],
  };
});

const toggleSidebar = () => {
  isExpanded.value = !isExpanded.value;
};

/* Whenever we add a table, we need to reset it's page from here.
So we'll need to add the store in the RESET_PAGINATION_ROUTE */
const RESET_PAGINATION_STORES = {
  '/statements': statementsStore,
  '/transfers': transfersStore,
  '/agencies': agenciesStore,
};

const getResetAction = (page) => {
  const path = page?.path;

  const targetStore = RESET_PAGINATION_STORES[path];

  if (targetStore) {
    return () => {
      targetStore.setAtomic('shouldResetFilters', true);
    };
  }
  return null;
};

watch(
  () => route.meta,
  (newValue) => {
    isExpanded.value = newValue.sidebarExpanded ?? true;
    isLocked.value = newValue.sidebarLocked ?? false;
  }
);
</script>
